import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Typography,
  Tag,
  Space,
  Row,
  Col,
  Statistic,
  Switch,
  Input,
  Select,
  Badge,
  Avatar,
  Button,
  Tooltip
} from 'antd';
import {
  ApiOutlined,
  UserOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { io } from 'socket.io-client';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const ApiActivity = () => {
  const [activities, setActivities] = useState([]);
  const [metrics, setMetrics] = useState({
    totalRequests: 0,
    averageResponseTime: 0,
    activeConnections: 0
  });
  const [isConnected, setIsConnected] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterMethod, setFilterMethod] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [maxRows, setMaxRows] = useState(100);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState('Component loaded');
  
  const socketRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    console.log('ApiActivity component mounted');
    setDebugInfo('Initializing Socket.IO connection...');
    
    const connectToBackend = () => {
      try {
        const envSocketUrl = process.env.REACT_APP_SOCKET_URL?.trim();
        const currentOrigin = window.location.origin; // preserves https -> wss
        const currentProtocol = window.location.protocol;
        const currentHostname = window.location.hostname;
        const preferredPort = process.env.REACT_APP_BACKEND_PORT || '3000';
        const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(currentHostname);

        const candidateUrls = [];
        if (envSocketUrl) {
          candidateUrls.push(envSocketUrl);
        }
        if (currentOrigin) {
          candidateUrls.push(currentOrigin);
        }
        // Same host + explicit backend port (useful when frontend served via proxy but backend exposed separately)
        if (currentHostname) {
          candidateUrls.push(`${currentProtocol}//${currentHostname}:${preferredPort}`);
        }
        // Local fallbacks for dev
        candidateUrls.push('http://localhost:3000', 'http://127.0.0.1:3000');
        
        // Remove duplicates while preserving order
        const connectionUrls = candidateUrls.filter((url, index) => url && candidateUrls.indexOf(url) === index);
        
        let attemptIndex = 0;
        
        const tryConnection = (url) => {
          console.log(`Attempting Socket.IO connection to: ${url}`);
          setDebugInfo(`Trying connection to: ${url}`);
          
          const socket = io(url, {
            transports: ['websocket', 'polling'], // Try both transport methods
            timeout: 5000,
            forceNew: true,
            reconnection: true,
            reconnectionAttempts: 3,
            reconnectionDelay: 1000
          });
          
          socket.on('connect', () => {
            console.log('Successfully connected to API Activity monitor at:', url);
            setIsConnected(true);
            setDebugInfo(`Connected to Socket.IO server at ${url}`);
            setError(null);
            socketRef.current = socket;
          });

          socket.on('disconnect', (reason) => {
            console.log('Disconnected from API Activity monitor:', reason);
            setIsConnected(false);
            setDebugInfo(`Disconnected: ${reason}`);
          });

          socket.on('connect_error', (err) => {
            console.error(`Socket.IO connection error for ${url}:`, err);
            
            // Try next URL if available
            attemptIndex++;
            if (attemptIndex < connectionUrls.length) {
              socket.disconnect();
              setTimeout(() => tryConnection(connectionUrls[attemptIndex]), 1000);
            } else {
              setError(`Connection failed: ${err.message}. Make sure the backend Socket.IO endpoint is reachable (tried: ${connectionUrls.join(', ')})`);
              setDebugInfo(`All connection attempts failed. Last error: ${err.message}`);
            }
          });

          socket.on('api-activity', (activityData) => {
            console.log('Received API activity:', activityData);
            if (!isPaused) {
              setActivities(prev => {
                const newActivities = [activityData, ...prev];
                // Keep only the latest maxRows entries
                return newActivities.slice(0, maxRows);
              });
            }
          });

          socket.on('api-metrics', (metricsData) => {
            console.log('Received API metrics:', metricsData);
            setMetrics(prev => ({
              ...prev,
              ...metricsData
            }));
          });
          
          return socket;
        };
        
        // Start with the first URL
        tryConnection(connectionUrls[0]);

      } catch (err) {
        console.error('Error setting up Socket.IO:', err);
        setError(`Setup error: ${err.message}`);
        setDebugInfo(`Setup error: ${err.message}`);
      }
    };
    
    connectToBackend();

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [isPaused, maxRows]);

  const getStatusColor = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return 'success';
    if (statusCode >= 300 && statusCode < 400) return 'warning';
    if (statusCode >= 400 && statusCode < 500) return 'error';
    if (statusCode >= 500) return 'error';
    return 'default';
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'blue',
      POST: 'green', 
      PUT: 'orange',
      DELETE: 'red',
      PATCH: 'purple'
    };
    return colors[method] || 'default';
  };

  const getResponseTimeColor = (time) => {
    if (time < 100) return '#52c41a'; // Green
    if (time < 500) return '#faad14'; // Yellow
    if (time < 1000) return '#fa8c16'; // Orange
    return '#f5222d'; // Red
  };

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = !searchText || 
      activity.route?.toLowerCase().includes(searchText.toLowerCase()) ||
      activity.user?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      activity.ip?.includes(searchText);
    
    const matchesMethod = filterMethod === 'all' || activity.method === filterMethod;
    const matchesUser = filterUser === 'all' || activity.user?.email === filterUser;
    
    return matchesSearch && matchesMethod && matchesUser;
  });

  const uniqueUsers = [...new Set(activities.map(a => a.user?.email).filter(Boolean))];
  const avgResponseTime = activities.length > 0 
    ? Math.round(activities.reduce((sum, a) => sum + a.responseTime, 0) / activities.length)
    : 0;

  const columns = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 100,
      render: (timestamp) => (
        <Text style={{ fontSize: '11px', fontFamily: 'monospace' }}>
          {new Date(timestamp).toLocaleTimeString()}
        </Text>
      )
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 80,
      render: (method) => (
        <Tag color={getMethodColor(method)} style={{ fontSize: '10px', margin: 0 }}>
          {method}
        </Tag>
      )
    },
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      width: 250,
      render: (route) => (
        <Text 
          style={{ 
            fontSize: '12px', 
            fontFamily: 'monospace',
            wordBreak: 'break-all'
          }}
        >
          {route}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'statusCode',
      key: 'statusCode',
      width: 80,
      render: (statusCode, record) => (
        <Tag 
          color={getStatusColor(statusCode)}
          icon={record.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          style={{ fontSize: '10px', margin: 0 }}
        >
          {statusCode}
        </Tag>
      )
    },
    {
      title: 'Time',
      dataIndex: 'responseTime',
      key: 'responseTime',
      width: 70,
      render: (time) => (
        <Text 
          style={{ 
            fontSize: '11px',
            color: getResponseTimeColor(time),
            fontWeight: '500'
          }}
        >
          {time}ms
        </Text>
      )
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      width: 120,
      render: (user) => (
        user ? (
          <Space size="small">
            <Avatar size={16} icon={<UserOutlined />} />
            <Text style={{ fontSize: '11px' }}>
              {`${user.name} ${user.surname || ''}`.trim()}
            </Text>
          </Space>
        ) : (
          <Text style={{ fontSize: '11px', color: '#999' }}>Anonymous</Text>
        )
      )
    },
    {
      title: 'IP Address',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
      render: (ip) => (
        <Text style={{ fontSize: '11px', fontFamily: 'monospace' }}>
          {ip}
        </Text>
      )
    }
  ];

  const clearActivities = () => {
    setActivities([]);
  };

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title="API Activity Monitor"
        subtitle="Real-time monitoring of all API calls and system activity"
        breadcrumb={BREADCRUMB_PATTERNS.team}
      />

      {/* Debug Info */}
      {(error || debugInfo) && (
        <Card style={{ marginBottom: '16px', backgroundColor: error ? '#fff2f0' : '#f6ffed' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Debug Info:</Text>
            <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>
              {error || debugInfo}
            </Text>
            {error && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Confirm the backend Socket.IO server is reachable from this browser (same origin in production, port 3000 locally).
              </Text>
            )}
          </Space>
        </Card>
      )}

      {/* Metrics Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Connection"
              value={isConnected ? 'Connected' : 'Disconnected'}
              prefix={
                <Badge 
                  status={isConnected ? 'success' : 'error'} 
                  style={{ marginRight: 8 }}
                />
              }
              valueStyle={{ 
                fontSize: '16px',
                color: isConnected ? '#52c41a' : '#f5222d'
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Requests"
              value={activities.length}
              prefix={<ApiOutlined />}
              valueStyle={{ fontSize: '16px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Avg Response Time"
              value={avgResponseTime}
              suffix="ms"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ fontSize: '16px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="Active Users"
              value={uniqueUsers.length}
              prefix={<UserOutlined />}
              valueStyle={{ fontSize: '16px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Controls */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Search
              placeholder="Search by route, user, or IP..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={4}>
            <Select
              value={filterMethod}
              onChange={setFilterMethod}
              style={{ width: '100%' }}
              size="small"
            >
              <Option value="all">All Methods</Option>
              <Option value="GET">GET</Option>
              <Option value="POST">POST</Option>
              <Option value="PUT">PUT</Option>
              <Option value="DELETE">DELETE</Option>
              <Option value="PATCH">PATCH</Option>
            </Select>
          </Col>
          <Col xs={12} sm={4}>
            <Select
              value={filterUser}
              onChange={setFilterUser}
              style={{ width: '100%' }}
              size="small"
            >
              <Option value="all">All Users</Option>
              {uniqueUsers.map(email => (
                <Option key={email} value={email}>{email}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <Space>
              <Tooltip title={isPaused ? "Resume monitoring" : "Pause monitoring"}>
                <Button
                  icon={isPaused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                  onClick={() => setIsPaused(!isPaused)}
                  type={isPaused ? "primary" : "default"}
                >
                  {isPaused ? 'Resume' : 'Pause'}
                </Button>
              </Tooltip>
              <Tooltip title="Clear all activities">
                <Button
                  icon={<ClearOutlined />}
                  onClick={clearActivities}
                  danger
                >
                  Clear
                </Button>
              </Tooltip>
              <Text style={{ fontSize: '12px' }}>
                Showing {filteredActivities.length} of {activities.length}
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Activity Table */}
      <Card>
        <Table
          ref={tableRef}
          dataSource={filteredActivities}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ y: 400 }}
          rowClassName={(record) => 
            record.success ? '' : 'error-row'
          }
          style={{
            fontFamily: 'monospace'
          }}
        />
      </Card>

      <style>
        {`
          .error-row {
            background-color: #fff1f0 !important;
          }
          .error-row:hover {
            background-color: #ffccc7 !important;
          }
        `}
      </style>
    </div>
  );
};

export default ApiActivity;